import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import API_BASE_URL from '../../components/apiconfig/api-config';
import SearchableDropdown from '../../components/common/SearchableDropdown';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const CostBreakupForm = () => {
  const [formData, setFormData] = useState({
    order_id: '',
    component_code: '',
    amount_inr: '',
    currency: '',
    fx_rate: '',
    warehouse_entry_id: '',
    remarks: '',
    component_type: '',
  });

  const [componentOptions, setComponentOptions] = useState([]);
  const [warehouseOptions, setWarehouseOptions] = useState([]);
  const [orderOptions, setOrderOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  // const [message, setMessage] = useState(null); // No longer needed for errors, only for success if you want
  const [fetchingComponents, setFetchingComponents] = useState(false);
  const [fetchingWarehouses, setFetchingWarehouses] = useState(false);
  const [fetchingOrders, setFetchingOrders] = useState(false);
  const navigate = useNavigate();

  const FIELD_LIMITS = {
    order_id: 10, // int, UI limit
    component_code: 30,
    amount_inr: 14, // decimal(14,2), input type number
    currency: 10,
    fx_rate: 10, // decimal(10,4), input type number
    warehouse_entry_id: 10, // int, UI limit
    remarks: 1000, // text, UI limit
    component_type: 50,
  };

  const validateField = (name, value) => {
    let error = '';
    const limit = FIELD_LIMITS[name];
    if (limit && value && value.length > limit) {
      error = `Maximum ${limit} characters allowed for ${name.replace(/_/g, ' ')}`;
    }
    // Required fields
    const requiredFields = ['order_id', 'component_code', 'amount_inr'];
    if (requiredFields.includes(name) && (!value || value === '' || value === 0)) {
      error = `${name.replace(/_/g, ' ')} is required`;
    }
    return error;
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const error = validateField(name, value);
    if (error) toast.error(error);
  };

  // Fetch all dropdown data when component mounts
  useEffect(() => {
    const fetchComponentCodes = async () => {
      setFetchingComponents(true);
      try {
        const res = await fetch(`${API_BASE_URL}/api/masters/dropdown-cost-component`, {
          credentials: 'include'
        });
        const data = await res.json();
        if (res.ok) {
          setComponentOptions(data.data);
        } else {
          console.error('Failed to fetch component codes:', data.error);
        }
      } catch (err) {
        console.error('Error fetching component codes:', err);
      } finally {
        setFetchingComponents(false);
      }
    };

    const fetchWarehouseEntries = async () => {
      setFetchingWarehouses(true);
      try {
        const res = await fetch(`${API_BASE_URL}/api/masters/dropdown-warehouse-entry`, {
          credentials: 'include'
        });
        const data = await res.json();
        if (res.ok) {
          setWarehouseOptions(data.data);
        } else {
          console.error('Failed to fetch warehouse entries:', data.error);
        }
      } catch (err) {
        console.error('Error fetching warehouse entries:', err);
      } finally {
        setFetchingWarehouses(false);
      }
    };

    const fetchImportOrders = async () => {
      setFetchingOrders(true);
      try {
        const res = await fetch(`${API_BASE_URL}/api/masters/dropdown-import-order`, {
          credentials: 'include'
        });
        const data = await res.json();
        if (res.ok) {
          setOrderOptions(data.data);
        } else {
          console.error('Failed to fetch import orders:', data.error);
        }
      } catch (err) {
        console.error('Error fetching import orders:', err);
      } finally {
        setFetchingOrders(false);
      }
    };

    fetchComponentCodes();
    fetchWarehouseEntries();
    fetchImportOrders();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDropdownChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    // setMessage(null);
    // Validate all fields
    for (const key in formData) {
      if (FIELD_LIMITS[key]) {
        const error = validateField(key, formData[key]);
        if (error) {
          toast.error(error);
          setLoading(false);
          return;
        }
      }
    }
    try {
      const res = await fetch(`${API_BASE_URL}/api/cost/cost_breakup_post`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData),
      });
      
      const data = await res.json();
      if (res.ok) {
        toast.success('Cost Breakup Created Successfully!');
        setFormData({
          order_id: '',
          component_code: '',
          amount_inr: '',
          currency: '',
          fx_rate: '',
          warehouse_entry_id: '',
          remarks: '',
          component_type: '',
        });
      } else {
        toast.error(data.error || 'Something went wrong');
      }
    } catch (err) {
      toast.error('Server Error');
    }
    setLoading(false);
  };

  return (
    <div className="max-w-8xl mx-auto p-0 bg-white">
      <div className="flex justify-between items-center border-b border-gray-100 bg-gray-50 px-4 py-1.5">
        <h2 className="text-lg font-bold mb-0 text-left">Cost Breakup Entry</h2>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-[#10a5e9] border border-[#10a5e9] px-2 py-0.5 rounded text-xs font-medium hover:bg-[#eaf6fd]"
        >
          <FaArrowLeft />
          Back
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-0">
        <div className="px-4 pt-4 pb-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium mb-1">Order ID</label>
              <SearchableDropdown
                options={orderOptions.map(order => ({
                  id: order.order_id,
                  name: `Order ID: ${order.order_id} | Contract: ${order.purchase_contract_no || 'N/A'}`
                }))}
                value={formData.order_id}
                onChange={(value) => handleDropdownChange('order_id', value)}
                onBlur={handleBlur}
                placeholder="Select an order"
                displayKey="name"
                valueKey="id"
                required
                disabled={fetchingOrders}
              />
              {fetchingOrders && (
                <p className="text-xs text-gray-500 mt-1">Loading orders...</p>
              )}
            </div>

            <div>
              <label className="block text-xs font-medium mb-1">Component Code</label>
              <select
                name="component_code"
                value={formData.component_code}
                onChange={handleChange}
                onBlur={handleBlur}
                maxLength={FIELD_LIMITS.component_code}
                className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none"
                required
                disabled={fetchingComponents}
              >
                <option value="">Select a component</option>
                {componentOptions.map((component) => (
                  <option key={component.component_code} value={component.component_code}>
                    {component.component_code}
                  </option>
                ))}
              </select>
              {fetchingComponents && (
                <p className="text-xs text-gray-500 mt-1">Loading components...</p>
              )}
            </div>

            <div>
              <label className="block text-xs font-medium mb-1">Amount (INR)</label>
              <input 
                type="number" 
                step="0.01"
                name="amount_inr" 
                value={formData.amount_inr} 
                onChange={handleChange} 
                onBlur={handleBlur}
                className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none" 
                required 
              />
            </div>

            <div>
              <label className="block text-xs font-medium mb-1">Currency</label>
              <input 
                type="text" 
                name="currency" 
                value={formData.currency} 
                onChange={handleChange} 
                onBlur={handleBlur}
                maxLength={FIELD_LIMITS.currency}
                className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none" 
              />
            </div>

            <div>
              <label className="block text-xs font-medium mb-1">FX Rate</label>
              <input 
                type="number" 
                step="0.0001"
                name="fx_rate" 
                value={formData.fx_rate} 
                onChange={handleChange} 
                onBlur={handleBlur}
                className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none" 
              />
            </div>

            <div>
              <label className="block text-xs font-medium mb-1">Warehouse Entry ID</label>
              <SearchableDropdown
                options={warehouseOptions.map(warehouse => ({
                  id: warehouse.warehouse_entry_id,
                  name: warehouse.warehouse_entry_id.toString()
                }))}
                value={formData.warehouse_entry_id}
                onChange={(value) => handleDropdownChange('warehouse_entry_id', value)}
                onBlur={handleBlur}
                placeholder="Select a warehouse entry"
                displayKey="name"
                valueKey="id"
                disabled={fetchingWarehouses}
              />
              {fetchingWarehouses && (
                <p className="text-xs text-gray-500 mt-1">Loading warehouse entries...</p>
              )}
            </div>

            <div>
              <label className="block text-xs font-medium mb-1">Component Type</label>
              <input 
                type="text" 
                name="component_type" 
                value={formData.component_type} 
                onChange={handleChange} 
                onBlur={handleBlur}
                maxLength={FIELD_LIMITS.component_type}
                className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none" 
              />
            </div>
          </div>

          <div className="mt-3">
            <label className="block text-xs font-medium mb-1">Remarks</label>
            <textarea 
              name="remarks" 
              value={formData.remarks} 
              onChange={handleChange} 
              onBlur={handleBlur}
              maxLength={FIELD_LIMITS.remarks}
              className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none resize-none" 
              rows={3}
            ></textarea>
          </div>
        </div>

        <div className="px-4 pt-2 pb-4">
          <button
            type="submit"
            className="w-auto px-4 py-1.5 text-sm bg-[#10a5e9] text-white font-semibold tracking-wide rounded border border-[#10a5e9] hover:bg-[#0e8ac2] transition disabled:opacity-60"
            disabled={loading || fetchingComponents || fetchingWarehouses || fetchingOrders}
          >
            {loading ? 'Saving...' : 'Save Cost Breakup'}
          </button>
          <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop closeOnClick pauseOnFocusLoss draggable pauseOnHover />
        </div>
      </form>
    </div>
  );
};

export default CostBreakupForm;